import React, { useRef, useState, useEffect, useContext } from 'react';
import { Typography, Button, Box, TextField, IconButton, CircularProgress, Grid, Divider, Chip, Select, MenuItem } from '@mui/material';
import Header from "../../Layout/Header/header";
import Footer from "../../Layout/Footer/footer";
import Uploadicon from "../../images/uploadicon.svg";
import CloseIcon from '@mui/icons-material/Close';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WorkIcon from '@mui/icons-material/Work';
import TimerIcon from '@mui/icons-material/Timer';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import ConstructionIcon from '@mui/icons-material/Construction';
import ImageIcon from '@mui/icons-material/Image';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import axios from 'axios';
import toast from 'react-hot-toast';
import { AuthContext } from '../../context/authcontext'; 
import { API_CONFIG } from "../../api/apiUrl"
import { useNavigate } from 'react-router-dom';

interface State {
  id: number;
  name: string;
  isActive: boolean;
  isDeleted: boolean;
}

interface City {
  id: number;
  name: string;
  isActive: boolean;
  isDeleted: boolean;
  states?: {
    id: number;
    name: string | null;
    isActive: boolean | null;
    isDeleted: boolean | null;
  };
}

const ContractorsDetailsAdd = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    specialty: '', 
    address: '',
    pincode: '',
    stateId: '',
    cityId: '',
    projects: '',
    experience: '',
    rating: '',
    companyLogo: 'string', 
    imageUrl: 'logo url',
    createdBy: authContext?.user?.id?.toString() || '',
    userProfile: authContext?.userProfile || null,
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [files, setFiles] = useState<{ file: File; url: string; previewUrl: string }[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [filteredCities, setFilteredCities] = useState<City[]>([]);

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await axios.get<State[]>(API_CONFIG.STATES);
        setStates(response.data);
      } catch (error) {
        toast.error('Failed to fetch states');
      }
    };
    const fetchCities = async () => {
      try {
        const response = await axios.get<City[]>(API_CONFIG.CITIES);
        setCities(response.data);
      } catch (error) {
        toast.error('Failed to fetch cities');
      }
    };
    fetchStates();
    fetchCities();
  }, []);

  useEffect(() => {
    if (formData.stateId) {
      const stateIdNum = parseInt(formData.stateId);
      setFilteredCities(cities.filter(city => city.states && city.states.id === stateIdNum));
    } else {
      setFilteredCities([]);
    }
  }, [formData.stateId, cities]);

  useEffect(() => {
    if (authContext?.user && authContext?.userProfile) {
      setFormData((prev) => ({
        ...prev,
        createdBy: authContext.user ? authContext.user.id.toString() : '',
        userProfile: authContext.userProfile,
      }));
    }
  }, [authContext?.user, authContext?.userProfile]);

  const validateField = (name: string, value: string) => {
    let error = '';
    if (name === 'name' && !value.trim()) error = 'Company name is required';
    if (name === 'address' && !value.trim()) error = 'Address is required';
    if (name === 'pincode') {
      if (!value.trim()) error = 'Pincode is required';
      else if (!/^\d{6}$/.test(value)) error = 'Pincode must be 6 digits';
    }
    if (name === 'stateId' && !value) error = 'State is required';
    if (name === 'cityId' && !value) error = 'City is required';
    if (name === 'specialty' && !value.trim()) error = 'Specialty is required';
    if (name === 'rating' && value) {
      const ratingNum = parseFloat(value);
      if (ratingNum < 0 || ratingNum > 5) error = 'Rating must be between 0 and 5';
    }
    return error;
  };

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    let newValue = value;
    if (name === 'pincode') newValue = value.replace(/[^0-9]/g, '').slice(0, 6);
    setFormData({ ...formData, [name]: newValue });
    setFormErrors({ ...formErrors, [name]: validateField(name, newValue) });
    if (name === 'stateId') setFormData(prev => ({ ...prev, cityId: '' }));
  };

  const handleNumericChange = (e: any) => {
    const { name, value } = e.target;
    let numericValue = value.replace(/[^0-9.]/g, '');
    if (name === 'pincode') numericValue = numericValue.slice(0, 6);
    setFormData({ ...formData, [name]: numericValue });
    setFormErrors({ ...formErrors, [name]: validateField(name, numericValue) });
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    Object.entries(formData).forEach(([name, value]) => {
      errors[name] = validateField(name, value as string);
    });
    setFormErrors(errors);
    return Object.values(errors).every(error => !error);
  };

  const uploadFile = async (file: File): Promise<string | null> => {
    const formData = new FormData();
    formData.append('file', file);
    const token = localStorage.getItem('id_token');
    try {
      const response = await axios.post<string>(API_CONFIG.FILE_UPLOAD, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch {
      toast.error('Failed to upload file');
      return null;
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setIsLoading(true);
      const uploadedFiles: { file: File; url: string; previewUrl: string }[] = [];
      for (const file of newFiles) {
        if (file.size <= 10 * 1024 * 1024) {
          const uploadedUrl = await uploadFile(file);
          if (uploadedUrl) {
            uploadedFiles.push({ file, url: uploadedUrl, previewUrl: URL.createObjectURL(file) });
            if (uploadedFiles.length === 1) setFormData(prev => ({ ...prev, companyLogo: uploadedUrl }));
          }
        } else {
          toast.error(`File ${file.name} exceeds the 10MB limit`);
        }
      }
      setFiles([...files, ...uploadedFiles]);
      if (uploadedFiles.length > 0) {
        setUploadSuccess(true);
        toast.success('Files uploaded successfully!');
        setTimeout(() => setUploadSuccess(false), 3000);
      }
      setIsLoading(false);
    }
  };

  const removeFile = (index: number) => {
    const newFiles = files.slice();
    URL.revokeObjectURL(newFiles[index].previewUrl);
    newFiles.splice(index, 1);
    setFiles(newFiles);
    toast.success('File removed');
  };

  // Function to initialize default rate cards for new company profile
  const initializeDefaultRateCards = async (companyProfileId: number) => {
    const token = localStorage.getItem('id_token');
    console.log('Starting rate cards initialization for company profile:', companyProfileId);
    
    try {
      // Fetch rate card types to get default structure
      console.log('Fetching rate card types...');
      const rateCardTypesResponse = await axios.get(API_CONFIG.RATE_CARD_TYPES, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      console.log('Rate card types response:', rateCardTypesResponse.data);
      const rateCardTypes = rateCardTypesResponse.data.content || rateCardTypesResponse.data || [];
      
      if (!rateCardTypes.length) {
        console.log('No rate card types found');
        return;
      }

      // Define default pricing and descriptions for Standard and Premium plans
      const defaultPlanData: { [key: string]: { price: number; description: string } } = {
        'Standard': {
          price: 1500,
          description: 'Per Square Feet - Blocks-Standard Solid Concrete blocks. 6 inch & 4 inch - Ceiling Height-10 feet (Finished Floor level to Finished Floor level) - Cement-Ambuja or Dalmia or equivalent 43 or 53 grade - Steel-Cement-Rathi, or equivalent'
        },
        'Premium': {
          price: 2000,
          description: 'Per Square Feet - Blocks-Standard Red Bricks. 9 inch & 4 inch - Ceiling Height- 10 feet (Finished Floor level to Finished Floor level) - Cement-Ambuja or Dalmia or equivalent 43 or 53 grade'
        }
      };

      // Create default rate cards for each type
      for (const rateCardType of rateCardTypes) {
        console.log(`Processing rate card type: ${rateCardType.name} (ID: ${rateCardType.id})`);
        
        try {
          // Fetch rate card items for this type
          const rateCardItemsResponse = await axios.get(
            API_CONFIG.RATE_CARD_ITEMS(rateCardType.id),
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          
          console.log(`Rate card items for ${rateCardType.name}:`, rateCardItemsResponse.data);
          const rateCardItems = rateCardItemsResponse.data.content || rateCardItemsResponse.data || [];

          // Get default price and description based on rate card type name
          const planData = defaultPlanData[rateCardType.name] || { 
            price: rateCardType.defaultPrice || 1000, 
            description: `Default ${rateCardType.name} plan` 
          };

          const payload = {
            rateCardTypes: {
              id: rateCardType.id,
            },
            companyRates: {
              price: planData.price,
              description: planData.description,
              companyProfile: {
                id: companyProfileId,
              },
              user: {
                id: authContext?.user?.id || parseInt(formData.createdBy) || 0,
                login: authContext?.user?.login || '',
                firstName: authContext?.user?.firstName || '',
              },
              rateCardTypes: {
                id: rateCardType.id,
              },
            },
            rateCardItems: rateCardItems.map((item: any) => ({
              rateCardItems: {
                id: item.id,
              },
            })),
          };

          console.log(`Posting rate card payload for ${rateCardType.name}:`, JSON.stringify(payload, null, 2));

          const response = await axios.post(API_CONFIG.COMPANY_RATES, payload, {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
          
          console.log(`Successfully created rate card for ${rateCardType.name}:`, response.data);
          
        } catch (error: any) {
          console.error(`Failed to create rate card for type ${rateCardType.name}:`, error);
          console.error('Error details:', error.response?.data || error.message);
        }
      }
      console.log('Default rate cards initialization completed');
    } catch (error: any) {
      console.error('Failed to initialize default rate cards:', error);
      console.error('Error details:', error.response?.data || error.message);
    }
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error('Please fill all required fields');
      return;
    }
    setIsLoading(true);
    const token = localStorage.getItem('id_token');
    const selectedCity = cities.find(c => c.id === parseInt(formData.cityId));
    const selectedState = states.find(s => s.id === parseInt(formData.stateId));
    const companyProfileData = {
      name: formData.name,
      description: formData.description,
      speciality: formData.specialty,
      address: formData.address,
      pincode: parseInt(formData.pincode) || 0,
      projects: parseInt(formData.projects) || 0,
      experience: parseInt(formData.experience) || 0,
      rating: parseFloat(formData.rating) || 0,
      companyLogo: formData.companyLogo,
      imageUrl: formData.imageUrl,
      isActive: true,
      isDeleted: false,
      cities: {
        id: parseInt(formData.cityId) || 0,
        name: selectedCity?.name || '',
        isActive: selectedCity?.isActive || true,
        isDeleted: selectedCity?.isDeleted || false,
        states: {
          id: parseInt(formData.stateId) || 0,
          name: selectedState?.name || '',
          isActive: selectedState?.isActive || true,
          isDeleted: selectedState?.isDeleted || false,
        },
      },
      createdBy: {
        id: parseInt(formData.createdBy) || 0,
        login: authContext?.user?.login || '',
      },
      userProfile: {
        id: formData.userProfile?.id || 0,
        mobile: formData.userProfile?.mobile || '',
        isVerified: formData.userProfile?.isVerified || false,
      },
      files: files.map(fileObj => fileObj.url),
    };
    try {
      const response = await axios.post(API_CONFIG.COMPANY_PROFILES_POST, companyProfileData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.status === 200 || response.status === 201) {
        const createdCompanyProfile = response.data;
        toast.success('Contractor details saved successfully!');
        
        // Initialize default rate cards for the newly created company profile
        if (createdCompanyProfile && createdCompanyProfile.id) {
          await initializeDefaultRateCards(createdCompanyProfile.id);
          toast.success('Default price cards initialized!');
        }
        
        setFormData({
          ...formData,
          name: '',
          description: '',
          specialty: '',
          address: '',
          pincode: '',
          stateId: '',
          cityId: '',
          projects: '',
          experience: '',
          rating: '',
        });
        setFiles([]);
        navigate('/contractors-profile');
      }
    } catch {
      toast.error('Failed to save contractor details');
    } finally {
      setIsLoading(false);
    }
  };

  const isButtonDisabled = !formData.name || !formData.stateId || !formData.cityId || !formData.specialty || !formData.address || !formData.pincode || !authContext?.isAuthenticated;

  if (authContext?.loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!authContext?.isAuthenticated) {
    return (
      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Typography variant="h6">Please log in to add contractor details.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: '2000px', margin: '0 auto' }}>
      <Header />
      <Box sx={{ py: { xs: 2, sm: 3, md: 3, lg: 3, xl: 3 }, px: { xs: 1, sm: 0.8, md: 2, lg: 4, xl: 6 } }}>
        <Box sx={styles.mainContainer}>
          <Box sx={styles.header}>
            <Typography sx={{ fontSize: { xs: '20px', sm: '25px', md: '25px' }, fontWeight: 500, lineHeight: { xs: 1.4, sm: 1.5, md: 1.6 } }}>
              Contractor Details
            </Typography>
            <Typography variant="body1" sx={{ color: 'white' }}>
              Join our network of professional contractors
            </Typography>
          </Box>
          <Box sx={{ padding: { xs: 2, sm: 3, md: 4 } }}>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" fontWeight={600} sx={{ mb: 2, color: '#89652A', display: 'flex', alignItems: 'center' }}>
                    <BusinessIcon sx={{ mr: 1 }} /> Company Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography sx={styles.inputLabel}>Company Name</Typography>
                  <TextField
                    fullWidth
                    name="name"
                    placeholder="Enter company name"
                    variant="outlined"
                    size="medium"
                    sx={styles.textField}
                    onChange={handleChange}
                    value={formData.name}
                    error={!!formErrors.name}
                    helperText={formErrors.name}
                    InputProps={{ startAdornment: <BusinessIcon sx={{ mr: 1, color: '#89652A' }} /> }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography sx={styles.inputLabel}>Address</Typography>
                  <TextField
                    fullWidth
                    name="address"
                    placeholder="Enter address"
                    variant="outlined"
                    size="medium"
                    sx={styles.textField}
                    onChange={handleChange}
                    value={formData.address}
                    error={!!formErrors.address}
                    helperText={formErrors.address}
                    InputProps={{ startAdornment: <LocationOnIcon sx={{ mr: 1, color: '#89652A' }} /> }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography sx={styles.inputLabel}>Pincode</Typography>
                  <TextField
                    fullWidth
                    name="pincode"
                    placeholder="Enter pincode"
                    variant="outlined"
                    size="medium"
                    sx={styles.textField}
                    onChange={handleChange}
                    value={formData.pincode}
                    error={!!formErrors.pincode}
                    helperText={formErrors.pincode}
                    inputProps={{ maxLength: 6 }}
                    InputProps={{ startAdornment: <LocationOnIcon sx={{ mr: 1, color: '#89652A' }} /> }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography sx={styles.inputLabel}>State</Typography>
                  <Select
                    fullWidth
                    name="stateId"
                    value={formData.stateId}
                    onChange={handleChange}
                    displayEmpty
                    sx={styles.select}
                    error={!!formErrors.stateId}
                  >
                    <MenuItem value="" disabled>Select State</MenuItem>
                    {states.map((state) => (
                      <MenuItem key={state.id} value={state.id}>{state.name}</MenuItem>
                    ))}
                  </Select>
                  {formErrors.stateId && (
                    <Typography color="error" variant="caption" sx={{ ml: 2 }}>
                      {formErrors.stateId}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography sx={styles.inputLabel}>City</Typography>
                  <Select
                    fullWidth
                    name="cityId"
                    value={formData.cityId}
                    onChange={handleChange}
                    displayEmpty
                    sx={styles.select}
                    error={!!formErrors.cityId}
                    disabled={!formData.stateId}
                  >
                    <MenuItem value="" disabled>Select City</MenuItem>
                    {filteredCities.length > 0 ? (
                      filteredCities.map((city) => (
                        <MenuItem key={city.id} value={city.id}>{city.name}</MenuItem>
                      ))
                    ) : (
                      formData.stateId && <MenuItem disabled>No cities available for selected state</MenuItem>
                    )}
                  </Select>
                  {formErrors.cityId && (
                    <Typography color="error" variant="caption" sx={{ ml: 2 }}>
                      {formErrors.cityId}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Typography sx={styles.inputLabel}>Description</Typography>
                  <TextField
                    fullWidth
                    name="description"
                    placeholder="Enter company description"
                    variant="outlined"
                    size="medium"
                    sx={styles.textField}
                    onChange={handleChange}
                    value={formData.description}
                    multiline
                    rows={2}
                    InputProps={{ startAdornment: <FormatQuoteIcon sx={{ mr: 1, mt: 1, color: '#89652A' }} /> }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h6" fontWeight={600} sx={{ mt: 2, mb: 2, color: '#89652A', display: 'flex', alignItems: 'center' }}>
                    <ConstructionIcon sx={{ mr: 1 }} /> Professional Details
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography sx={styles.inputLabel}>Specialty</Typography>
                  <TextField
                    fullWidth
                    name="specialty"
                    placeholder="Enter specialty"
                    variant="outlined"
                    size="medium"
                    sx={styles.textField}
                    onChange={handleChange}
                    value={formData.specialty}
                    error={!!formErrors.specialty}
                    helperText={formErrors.specialty}
                    InputProps={{ startAdornment: <ConstructionIcon sx={{ mr: 1, color: '#89652A' }} /> }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography sx={styles.inputLabel}>Number of Projects</Typography>
                  <TextField
                    fullWidth
                    name="projects"
                    placeholder="Enter number of projects"
                    variant="outlined"
                    size="medium"
                    sx={styles.textField}
                    onChange={handleNumericChange}
                    value={formData.projects}
                    type="text"
                    InputProps={{ startAdornment: <WorkIcon sx={{ mr: 1, color: '#89652A' }} /> }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography sx={styles.inputLabel}>Years of Experience</Typography>
                  <TextField
                    fullWidth
                    name="experience"
                    placeholder="Enter years of experience"
                    variant="outlined"
                    size="medium"
                    sx={styles.textField}
                    onChange={handleNumericChange}
                    value={formData.experience}
                    type="text"
                    InputProps={{ startAdornment: <TimerIcon sx={{ mr: 1, color: '#89652A' }} /> }}
                  />
                </Grid>
                {/* <Grid item xs={12} md={4}>
                  <Typography sx={styles.inputLabel}>Rating</Typography>
                  <TextField
                    fullWidth
                    name="rating"
                    placeholder="Enter rating (0-5)"
                    variant="outlined"
                    size="medium"
                    sx={styles.textField}
                    onChange={handleNumericChange}
                    value={formData.rating}
                    type="text"
                    error={!!formErrors.rating}
                    helperText={formErrors.rating}
                    InputProps={{ startAdornment: <StarIcon sx={{ mr: 1, color: '#89652A' }} /> }}
                  />
                </Grid> */}
                <Grid item xs={12}>
                  <Typography variant="h6" fontWeight={600} sx={{ mt: 2, mb: 2, color: '#89652A', display: 'flex', alignItems: 'center' }}>
                    <ImageIcon sx={{ mr: 1 }} /> Portfolio Images
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>
                <Grid item xs={12}>
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    accept=".jpg,.jpeg,.png,.pdf"
                    onChange={handleFileChange}
                    multiple
                  />
                  <Box onClick={() => fileInputRef.current?.click()} sx={styles.uploadBox}>
                    <img src={Uploadicon} alt="Upload" style={{ height: '70px', margin: '10px 0' }} />
                    <Typography sx={{ color: '#B69D74', fontSize: '18px', fontWeight: 500, my: 1 }}>
                    </Typography>
                    <Typography sx={{ fontSize: '15px', display: 'inline', my: 0.3 }}>
                      <span style={{ textDecoration: 'underline', color: '#B69D74', fontWeight: 500 }}>
                        Browse Files
                      </span> From Your Computer
                    </Typography>
                    <Typography sx={{ fontSize: '14px', mt: 1, color: '#666' }}>
                      Max size 10 MB, Format: .jpg, .png, .pdf, .jpeg
                    </Typography>
                    {uploadSuccess && (
                      <Chip
                        icon={<CheckCircleIcon />}
                        label="Files uploaded successfully!"
                        color="success"
                        variant="outlined"
                        sx={{ mt: 2 }}
                      />
                    )}
                  </Box>
                </Grid>
                {files.length > 0 && (
                  <Grid item xs={12}>
                    <Typography sx={{ fontSize: '16px', fontWeight: 500, mb: 2, color: '#89652A' }}>
                      Uploaded Images ({files.length})
                    </Typography>
                    <Box sx={styles.imageGridContainer}>
                      {files.map((fileObj, index) => (
                        <Box key={index} sx={styles.imageGridItem}>
                          <img src={fileObj.previewUrl} alt={`Uploaded ${index}`} style={styles.gridImage} />
                          <IconButton sx={styles.removeImageButton} onClick={() => removeFile(index)}>
                            <CloseIcon sx={{ fontSize: 18, color: 'white' }} />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  </Grid>
                )}
                <Grid item xs={12} sx={{ textAlign: 'center', mt: 3 }}>
                  <Button
                    type="submit"
                    sx={styles.submitButton}
                    disabled={isButtonDisabled}
                    size="large"
                  >
                    {isLoading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Save Contractor Details'}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Box>
        </Box>
      </Box>
      <Footer />
    </Box>
  );
};

const styles: any = {
  mainContainer: { borderRadius: { xs: "10px", sm: "10px" }, boxShadow: '0px 0px 3px 0px #00000033', border: '0.75px solid #2D2D2D33' },
  header: { background: "linear-gradient(90deg, #89652A 0%, #B69D74 100%)", color: "#fff", padding: { xs: "10px 12px", sm: "14px 20px", md: "16px 24px" }, borderTopLeftRadius: { xs: "6px", sm: "8px" }, borderTopRightRadius: { xs: "6px", sm: "8px" } },
  textField: { 
    '& .MuiOutlinedInput-root': { 
      borderRadius: '12px', 
      borderColor: '#89652A', 
      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#B69D74', borderWidth: '1.5px' }, 
      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#89652A' }, 
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#89652A', borderWidth: '2px' },
      '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: '#B69D74' },
    }, 
    '& .MuiInputBase-input': { padding: '14px 14px' } 
  },
  select: { borderRadius: '12px', '& .MuiOutlinedInput-notchedOutline': { border: '1.5px solid #B69D74' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#89652A' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#89652A', borderWidth: '2px' }, '& .MuiSelect-select': { py: '14px' } },
  inputLabel: { fontSize: '16px', fontWeight: 500, mb: 1.5, color: '#333' },
  submitButton: { textTransform: 'capitalize', fontSize: { xs: '16px', md: '16px' }, fontWeight: 600, borderRadius: '28px', color: 'white', bgcolor: '#89652A', py: 0.7, px: { xs: 4, md: 6 }, '&:hover': { bgcolor: '#6b4e20' }, '&:disabled': { bgcolor: '#a88b5e' } },
  uploadBox: { border: '2px dashed #B69D74', borderRadius: '16px', p: 4, textAlign: 'center', mb: 2, cursor: 'pointer', transition: 'all 0.3s ease', bgcolor: 'rgba(182, 157, 116, 0.05)', '&:hover': { bgcolor: 'rgba(182, 157, 116, 0.1)', borderColor: '#89652A', transform: 'translateY(-2px)' } },
  imageGridContainer: { display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)', lg: 'repeat(5, 1fr)' }, gap: 2, mb: 3 },
  imageGridItem: { position: 'relative', borderRadius: '12px', overflow: 'hidden', aspectRatio: '1/1', boxShadow: '0 4px 8px rgba(0,0,0,0.1)' },
  gridImage: { width: '100%', height: '100%', objectFit: 'cover' },
  removeImageButton: { position: 'absolute', top: 8, right: 8, bgcolor: 'rgba(0,0,0,0.6)', width: 28, height: 28, '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' } },
};

export default ContractorsDetailsAdd;